import { UploadOutlined } from "@ant-design/icons";
import { Button, Checkbox, Form, Input, Modal, Select, Upload, message } from "antd";
import TextArea from "antd/lib/input/TextArea";
import { formatError } from "common/redux/middleware/queryErrorLogger";
import { useUserType } from "features/auth";
import {
  usePostDocumentsMutation,
  useUpdateDocumentMutation,
} from "features/documentsTable/agencyPortal/documentsAPI";
import { usePostCreditorPortalDebtorDocumentsMutation } from "features/documentsTable/creditorPortal/documentsAPI";
import { useFetchDebtorAccountsQuery } from "features/debtorAccountsTable/agencyPortal/debtorAccountsAPI";
import { useFetchCreditorPortalDebtorAccountsQuery } from "features/debtorAccountsTable/creditorPortal/debtorAccountsAPI";
import { useFetchBackendConstantsQuery } from "features/home/agencyPortal/homeAPI";
import { useMemo, useState } from "react";
import styled from "styled-components";

const StyledForm = styled(Form)`
  margin-top: 12px;
  max-width: 400px;
`;

const EDITABLE_FIELDS = ["type", "notes", "showOnConsumerPortal"];

function UploadDocumentModal({ debtorId, title, onOk, onCancel, selectedDocument }) {
  const { isAgencyUserType } = useUserType();
  const [uploadAgencyDocument, { isLoading: isUploadAgencyDocumentLoading }] =
    usePostDocumentsMutation();
  const [updateDocument, { isLoading: isUpdateDocumentLoading }] = useUpdateDocumentMutation();
  const [uploadCreditorPortalDocument, { isLoading: isUploadCreditorPortalDocumentLoading }] =
    usePostCreditorPortalDebtorDocumentsMutation();
  const uploadDocument = isAgencyUserType ? uploadAgencyDocument : uploadCreditorPortalDocument;
  const isUploadDocumentLoading = isAgencyUserType
    ? isUploadAgencyDocumentLoading
    : isUploadCreditorPortalDocumentLoading;
  const isLoading = isUploadDocumentLoading || isUpdateDocumentLoading;
  const [fileList, setFileList] = useState([]);
  const { data: constants } = useFetchBackendConstantsQuery();
  const { data: agencyDebtorAccounts } = useFetchDebtorAccountsQuery(
    { debtorId },
    { skip: !isAgencyUserType },
  );
  const { data: creditorPortalDebtorAccounts } = useFetchCreditorPortalDebtorAccountsQuery(
    { debtorId },
    { skip: isAgencyUserType },
  );
  const debtorAccounts = isAgencyUserType ? agencyDebtorAccounts : creditorPortalDebtorAccounts;
  const normFile = (e) => {
    if (Array.isArray(e)) {
      return e;
    }

    return e?.fileList;
  };

  const [form] = Form.useForm();

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      const action = selectedDocument
        ? updateDocument({
            debtorId,
            accountIds: values.accountIds,
            documentId: selectedDocument.id,
            type: values.type,
            notes: values.notes,
            showOnConsumerPortal: values.showOnConsumerPortal,
          })
        : uploadDocument({ ...values, debtorId, accountIds: values.accountIds || [] });
      const result = await action;
      if ("data" in result) {
        form.resetFields();
        onOk(); // Close the modal using the parent callback
      }
      if ("error" in result) {
        form.setFields(formatError(result.error));
      }
    } catch (error) {
      console.log("Validate Failed:", error);
    }
  };

  // See https://ant.design/components/form/ `validateMessages` to see how to customize error
  // messages.
  const validateMessages = {
    required: "This is a required field",
  };

  const initialValues = useMemo(() => {
    if (selectedDocument) {
      return {
        ...selectedDocument,
        file: [{ name: selectedDocument.filename }],
      };
    }
    return { showOnConsumerPortal: false };
  }, [selectedDocument]);

  return (
    <Modal
      maskClosable={false}
      title={title}
      open
      onOk={handleOk}
      onCancel={onCancel}
      okButtonProps={{ loading: isLoading, disabled: isLoading }}
    >
      <StyledForm
        layout="vertical"
        form={form}
        autoComplete="off"
        validateMessages={validateMessages}
        initialValues={initialValues}
      >
        <Form.Item
          label="Name"
          name="filename"
          rules={[{ required: true }]}
          tooltip="This is a required field"
        >
          <Input disabled={selectedDocument && !EDITABLE_FIELDS.includes("filename")} />
        </Form.Item>
        <Form.Item name="accountIds" label="Affected Accounts">
          <Select
            popupMatchSelectWidth={false}
            mode="multiple"
            allowClear
            options={debtorAccounts?.map((acct) => ({
              label: `Account #${acct.externalId}`,
              value: acct.id,
            }))}
            placeholder="Select Accounts..."
          />
        </Form.Item>
        <Form.Item
          label="Type"
          name="type"
          rules={[{ required: true }]}
          tooltip="This is a required field"
        >
          <Select
            showSearch
            filterOption={(input, option) =>
              (option.label.toString() ?? "").toLowerCase().includes(input.toLowerCase())
            }
            popupMatchSelectWidth={false}
            disabled={selectedDocument && !EDITABLE_FIELDS.includes("type")}
            options={constants?.documentTypes
              .filter((x) => x.value !== "account_bulk_upload" && x.value !== "dispute")
              .map((fileType) => ({
                label: fileType.display,
                value: fileType.value,
              }))}
          />
        </Form.Item>
        <Form.Item label="Notes" name="notes" initialValue="">
          <TextArea rows={4} disabled={selectedDocument && !EDITABLE_FIELDS.includes("notes")} />
        </Form.Item>
        <Form.Item
          label="Upload"
          name="file"
          valuePropName="fileList"
          getValueFromEvent={normFile}
          rules={[{ required: true }]}
        >
          <Upload
            multiple={false}
            name="file"
            headers={{
              authorization: "authorization-text",
            }}
            onChange={(info) => {
              const { status } = info.file;
              if (status === "done") {
                message.success(`${info.file.name} file uploaded successfully`);
              } else if (status === "error") {
                message.error(`${info.file.name} file upload failed.`);
              }
            }}
            beforeUpload={(file) => {
              setFileList([...fileList, file]);
              return false;
            }}
            disabled={selectedDocument && !EDITABLE_FIELDS.includes("file")}
          >
            {!(selectedDocument && !EDITABLE_FIELDS.includes("file")) && (
              <Button icon={<UploadOutlined />}>Click to Upload</Button>
            )}
          </Upload>
        </Form.Item>
        {isAgencyUserType && (
          <Form.Item name="showOnConsumerPortal" valuePropName="checked">
            <Checkbox
              disabled={selectedDocument && !EDITABLE_FIELDS.includes("showOnConsumerPortal")}
            >
              Show on Consumer Portal
            </Checkbox>
          </Form.Item>
        )}
        {/* TODO: Consider if we want to allow bulk upload documents. For now only single.
        <Form.Item label="Dragger">
          <Form.Item name="dragger" valuePropName="fileList" getValueFromEvent={normFile} noStyle>
            <Upload.Dragger name="files" action="/upload.do">
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">Click or drag file to this area to upload</p>
              <p className="ant-upload-hint">Support for a single or bulk upload.</p>
            </Upload.Dragger>
          </Form.Item>
        </Form.Item> */}
      </StyledForm>
    </Modal>
  );
}

export default UploadDocumentModal;
